import { NonIdealState } from '@blueprintjs/core';
import useUserContext from 'ecommerce/context/user';
import PropTypes from 'prop-types';
import React, { cloneElement, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import debounce from 'utils/debounce';

import useEditorItemContext from '../../../context/editorItemContext';
import PersonCardError from '../personCardError';
import PersonCardLoader from '../personCardLoader';
import PersonCardNoResult from '../personCardNoResult';
import PersonSearchForm from '../personSearchForm';
import ancestorsApi from '../../../api/ancestors';

/**
 * Number of milliseconds to wait for a next user search text input.
 * After that time passes the text value is used to search people on server.
 * @type {number}
 */
const debounceWait = 750;

/**
 * Minimal length of the search text string.
 * Shorter strings aren't sent to server, as it would return error.
 * This configuration is hard-coded on server, so it must be also hard-coded here.
 * @type {number}
 */
const searchTextMinLength = 3;

const StyledPersonSearchForm = styled(PersonSearchForm)`
  margin-bottom: 20px;
`;

const StyledPersonCardError = styled(PersonCardError)`
  margin-bottom: 20px;
`;

const StyledPersonCardLoader = styled(PersonCardLoader)`
  margin-bottom: 20px;
`;

const StyledPersonCardNoResult = styled(PersonCardNoResult)`
  margin-bottom: 20px;
`;

const StyledNonIdealState = styled(NonIdealState)`
  margin-bottom: 20px;
`;

const isValid = searchText => searchText.length >= searchTextMinLength;

const SOURCE_MESSAGES = {
  'ancestry': {
    searchTitle: 'Search your Ancestry family tree',
    searchDescription: `You must enter at least ${searchTextMinLength} characters to start searching your Ancestry tree.`,
    noResultsTitle: 'No people found in your Ancestry tree',
    noResultsDescription: 'Your search didn\'t match any people in your Ancestry tree. Try searching for something else.',
  },
  'familysearch': {
    searchTitle: 'Search your FamilySearch family tree',
    searchDescription: 'This search finds your direct ancestors (parents, grandparents, etc.) and excludes siblings or other collateral relatives.',
    noResultsTitle: 'No people found in your FamilySearch tree',
    noResultsDescription: 'Your search didn\'t match any people in your FamilySearch tree. Try searching for something else.',
  },
};

const AllPeoplePanel = ({ treeId, panel, personGroup, includeCitations = true }) => {
  const { accessToken, ancestryAuth } = useUserContext();
  const { config } = useEditorItemContext();

  const [abortController, setAbortController] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [people, setPeople] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [peopleError, setPeopleError] = useState();

  // Abort the current controller on unmount component, or when another controller is set.
  useEffect(() => () => abortController?.abort(), [abortController]);

  const fetchPeopleDebounced = useMemo(() => {
    const debounced = debounce(async (newAbortController, newSearchText) => {
      const { data, error } = await ancestorsApi(config.source, accessToken, ancestryAuth).getTreePersons(
        treeId,
        newSearchText,
        {
          signal: newAbortController.signal,
          includeCitations,
        },
      );

      if (error) {
        if (error.name === 'AbortError') {
          return;
        }
        setPeopleError(error);
        setIsLoading(false);
        return;
      }
      setPeopleError();
      setPeople(data);
      setIsLoading(false);
    }, debounceWait);
    return (...args) => {
      if (accessToken == null) {
        throw new Error('Missing access token.');
      }
      if (treeId == null) {
        throw new Error('Missing tree ID.');
      }
      // Immediately display loading spinner, cancel pending request and wait for a debounced input.
      setIsLoading(true);
      const newAbortController = new AbortController();
      setAbortController(newAbortController);
      return debounced(newAbortController, ...args);
    };
  }, [accessToken, ancestryAuth, treeId, includeCitations]);

  const handleChange = useCallback(
    newSearchText => {
      setSearchText(newSearchText);
      if (isValid(newSearchText)) {
        fetchPeopleDebounced(newSearchText);
        return;
      }
      setPeopleError();
      setPeople([]);
    },
    [fetchPeopleDebounced],
  );

  const handleReset = useCallback(() => setSearchText(''), []);

  const infoNode = useMemo(() => {
    const messages = SOURCE_MESSAGES[config.source] || SOURCE_MESSAGES['ancestry'];

    // When persons are are still loading, show spinner.
    if (isLoading) {
      return <StyledPersonCardLoader />;
    }
    if (peopleError) {
      return <StyledPersonCardError message={peopleError.message} />;
    }
    // When search text is not long enough, show warning and instructions what to do.
    if (!isValid(searchText)) {
      return (
        <StyledNonIdealState
          description={messages.searchDescription}
          icon="search"
          title={messages.searchTitle}
        />
      );
    }
    // All people are filtered out by search.
    if (people.length === 0) {
      return (
        <StyledPersonCardNoResult
          onReset={handleReset}
          title={messages.noResultsTitle}
          description={messages.noResultsDescription}
        />
      );
    }
    // People are displayed, no need to show info.
    return null;
  }, [handleReset, isLoading, people.length, peopleError, searchText, config.source]);

  return (
    <>
      <StyledPersonSearchForm onChange={handleChange} value={searchText} />
      {infoNode}
      {cloneElement(panel, { people, personGroup, includeCitations })}
    </>
  );
};

AllPeoplePanel.propTypes = {
  treeId: PropTypes.string.isRequired,
  panel: PropTypes.node.isRequired,
  personGroup: PropTypes.object.isRequired,
  includeCitations: PropTypes.bool,
};

export default AllPeoplePanel;
